import { useEffect, useMemo, useState } from "react";

import {
  IntelligemsAppScopeType,
  WebUiScopesConfigurationKeyType,
} from "@intelligems/ig-types";
import { getIntelligemsAppScopes } from "@intelligems/ig-utils-strict";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { FEATURE_TOGGLE_GLOBAL_DEBUG } from "@src/local-features";

import { useGetOrg } from "../../api/organizations/get-organization";
import { useIsAdmin } from "../../api/user-auths/is-admin";

export const useScope = (features?: WebUiScopesConfigurationKeyType[]) => {
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const organization = useGetOrg();

  const isAdmin = useIsAdmin();

  useEffect(() => {
    if (!features) {
      setHasAccess(false);
    } else if (FEATURE_TOGGLE_GLOBAL_DEBUG) {
      setHasAccess(false);
    } else if (isAdmin) {
      setHasAccess(true);
    } else {
      setHasAccess(
        organization.data.org.configurations.some(
          (config) =>
            features.some((permission) => permission === config.key) &&
            config.value === true
        )
      );
    }
  }, [organization.data.org.configurations, features, isAdmin]);

  return hasAccess;
};

export const useHasRequiredShopifyScopes = (requiredScopes: string[]) => {
  const organization = useGetOrg();

  return useMemo(() => {
    return requiredScopes.every((scope) =>
      organization.data.org.scopes?.includes(scope)
    );
  }, [organization.data.org.scopes, requiredScopes]);
};

/**
 * The canonical scopes this org is expected to have granted but has not. The
 * single source of truth for "is the org missing permissions" — the banner
 * copy, the banner's visibility, and anything else asking the question all
 * read it, so a scope's requirement is only ever encoded once.
 */
export const useMissingShopifyScopes = (): IntelligemsAppScopeType[] => {
  const organization = useGetOrg();
  const hasPostPurchaseAccess = useConfiguration(
    "enable_post_purchase_checkout"
  )?.value;
  const orgScopes = organization.data.org.scopes ?? [];

  return getIntelligemsAppScopes(import.meta.env.VITE_STAGE as string).filter(
    (scope) =>
      !orgScopes.includes(scope) &&
      // TODO: remove once pp is live to all
      (Boolean(hasPostPurchaseAccess) || scope !== "write_orders") &&
      scope !== "read_all_orders"
  );
};

export const useHasStorefrontToken = () => {
  const organization = useGetOrg();

  return useMemo(() => {
    return organization.data.org.hasStorefrontToken;
  }, [organization.data.org.hasStorefrontToken]);
};
